import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Users, ArrowRight, Check, Lock, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function nightsBetween(a, b) {
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
}

export default function BookingEngine() {
  const { hotelId } = useParams();
  const [hotel, setHotel] = useState(null);
  const [checkIn, setCheckIn] = useState(todayISO(1));
  const [checkOut, setCheckOut] = useState(todayISO(4));
  const [availability, setAvailability] = useState(null);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1); // 1=search,2=select,3=guest-info,4=success
  const [guest, setGuest] = useState({ name: "", email: "", phone: "", count: 2 });
  const [booking, setBooking] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const nights = useMemo(() => nightsBetween(checkIn, checkOut), [checkIn, checkOut]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${PUBLIC_API}/hotel/${hotelId}`);
        setHotel(data);
      } catch {
        toast.error("Hotel not found");
      }
    })();
  }, [hotelId]);

  async function searchAvailability() {
    if (nights <= 0) { toast.error("Choose valid dates"); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${PUBLIC_API}/availability/${hotelId}`, {
        params: { check_in: `${checkIn}T12:00:00`, check_out: `${checkOut}T11:00:00` },
      });
      setAvailability(data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  }

  async function startBooking() {
    if (!guest.name || !guest.email || !guest.phone) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${PUBLIC_API}/bookings/create-order`, {
        hotel_id: hotelId,
        room_type: selected.room_type,
        check_in: `${checkIn}T12:00:00`,
        check_out: `${checkOut}T11:00:00`,
        guest_name: guest.name,
        guest_email: guest.email,
        guest_phone: guest.phone,
        guests_count: Number(guest.count),
      });
      setBooking(data);
      await simulatePayment(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
      setLoading(false);
    }
  }

  async function simulatePayment(order) {
    try {
      const { data } = await axios.post(`${PUBLIC_API}/bookings/verify`, {
        reservation_id: order.reservation_id,
        razorpay_order_id: order.order_id,
        razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2, 10)}`,
        razorpay_signature: "mock_signature",
      });
      setBookingId(data.reservation_id);
      setStep(4);
      toast.success("Booking confirmed! Confirmation email sent.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment verification failed");
    } finally { setLoading(false); }
  }

  if (!hotel) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B101E] text-white/50">Loading hotel…</div>;
  }

  const total = selected ? selected.rate * nights : 0;

  return (
    <div className="min-h-screen bg-[#0B101E] text-[#F8F9FA]" data-testid="booking-engine-page">
      <nav className="border-b border-white/5 bg-[#0a0f1c]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="be-brand">
            <span className="w-8 h-8 rounded-md bg-[#D4AF37] flex items-center justify-center text-[#0B101E] font-bold font-display">B</span>
            <span className="font-display text-xl">Booking4U</span>
          </Link>
          <div className="text-xs text-white/50 flex items-center gap-2"><Lock className="w-3 h-3" /> Secure · Zero commission</div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Direct booking</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight" data-testid="hotel-name">{hotel.hotel.name}</h1>
          <div className="text-white/60 mt-3">{hotel.hotel.location}</div>

          {/* Step 1 - Dates */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10 bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="step-dates">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-4">Select your dates</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Check-in">
                  <input type="date" value={checkIn} min={todayISO(0)} onChange={(e) => setCheckIn(e.target.value)} className="input" data-testid="ci-input" />
                </Field>
                <Field label="Check-out">
                  <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} className="input" data-testid="co-input" />
                </Field>
                <Field label="Nights">
                  <div className="input flex items-center">{nights}</div>
                </Field>
              </div>
              <button onClick={searchAvailability} disabled={loading || nights <= 0} className="btn-gold mt-6 rounded-md px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="search-btn">
                {loading ? "Searching…" : <>Search rooms <ArrowRight className="w-4 h-4" /></>}
              </button>
            </motion.div>
          )}

          {/* Step 2 - Rooms */}
          {step === 2 && availability && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-4" data-testid="step-rooms">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Available rooms</div>
                  <div className="text-white/70 text-sm mt-1">{new Date(checkIn).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} → {new Date(checkOut).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · {nights} night{nights > 1 ? "s" : ""}</div>
                </div>
                <button onClick={() => setStep(1)} className="text-xs text-[#D4AF37] hover:text-[#F2D06B]">Change dates</button>
              </div>
              {availability.room_types.length === 0 && <div className="bg-[#12182B] border border-white/10 rounded-xl p-8 text-white/60">No rooms available for the selected dates.</div>}
              {availability.room_types.map((rt) => (
                <div key={rt.room_type} className="bg-[#12182B] border border-white/5 rounded-xl p-6 flex items-center justify-between hover:border-[#D4AF37]/30 transition" data-testid={`room-card-${rt.room_type.replace(/\s/g,'-')}`}>
                  <div>
                    <div className="font-display text-xl">{rt.room_type}</div>
                    <div className="text-xs text-white/50 mt-1">{rt.available_count} room{rt.available_count > 1 ? "s" : ""} available</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">₹{Math.round(rt.rate).toLocaleString("en-IN")}</div>
                    <div className="text-xs text-white/50">per night</div>
                    <button onClick={() => { setSelected(rt); setStep(3); }} className="btn-ghost-gold rounded-md px-4 py-2 mt-3 text-sm" data-testid={`select-${rt.room_type.replace(/\s/g,'-')}`}>
                      Select
                    </button>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Step 3 - Guest info & pay */}
          {step === 3 && selected && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10 bg-[#12182B] border border-white/5 rounded-xl p-6 space-y-5" data-testid="step-guest">
              <div className="text-xs uppercase tracking-widest text-white/50">Guest details</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name"><input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} className="input" data-testid="guest-name" /></Field>
                <Field label="Guests"><input type="number" min={1} max={6} value={guest.count} onChange={(e) => setGuest({ ...guest, count: e.target.value })} className="input" data-testid="guest-count" /></Field>
                <Field label="Email"><input type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} className="input" data-testid="guest-email" /></Field>
                <Field label="Phone"><input value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} className="input" data-testid="guest-phone" /></Field>
              </div>
              <div className="text-xs text-white/50 flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#D4AF37]" /> Demo checkout — payment is simulated in this environment</div>
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="btn-ghost-gold rounded-md px-5 py-3 text-sm">Back</button>
                <button onClick={startBooking} disabled={loading} className="btn-gold rounded-md px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="pay-btn">
                  <Lock className="w-4 h-4" />
                  {loading ? "Processing…" : `Pay ₹${total.toLocaleString("en-IN")}`}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 4 - Success */}
          {step === 4 && bookingId && (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="mt-10 bg-[#12182B] border border-[#D4AF37]/30 rounded-xl p-8 text-center" data-testid="step-success">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 mx-auto flex items-center justify-center text-[#D4AF37]">
                <Check className="w-7 h-7" strokeWidth={2} />
              </div>
              <h2 className="font-display text-3xl mt-5">Booking confirmed!</h2>
              <p className="text-white/60 mt-3 max-w-md mx-auto">A confirmation email has been sent to <span className="text-[#D4AF37]">{guest.email}</span>. Your booking reference:</p>
              <div className="font-display text-2xl text-[#D4AF37] mt-4 tracking-wider" data-testid="booking-ref">{bookingId.slice(0, 8).toUpperCase()}</div>
              <div className="mt-8">
                <Link to="/" className="btn-ghost-gold rounded-md px-5 py-3 inline-flex items-center gap-2">Back to Booking4U</Link>
              </div>
            </motion.div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="booking-summary">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Your stay</div>
            <div className="space-y-3 text-sm">
              <Row icon={CalendarIcon} label="Check-in" value={new Date(checkIn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />
              <Row icon={CalendarIcon} label="Check-out" value={new Date(checkOut).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />
              <Row icon={Users} label="Nights" value={`${nights} night${nights > 1 ? "s" : ""}`} />
              {selected && (
                <>
                  <div className="border-t border-white/5 pt-3" />
                  <Row label="Room" value={selected.room_type} />
                  <Row label="Rate / night" value={`₹${selected.rate.toLocaleString("en-IN")}`} />
                </>
              )}
            </div>
            {selected && (
              <>
                <div className="border-t border-white/5 my-5" />
                <div className="flex items-end justify-between">
                  <div className="text-xs uppercase tracking-widest text-white/50">Total</div>
                  <div className="font-display text-3xl">₹{total.toLocaleString("en-IN")}</div>
                </div>
                <div className="text-xs text-white/40 mt-2">Inclusive of all taxes · Zero commission · Free cancellation up to 48h</div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input { width: 100%; background: #0a0f1c; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px 12px; color: #F8F9FA; font-size: 14px; outline: none; transition: border-color .2s; }
        .input:focus { border-color: rgba(212, 175, 55, 0.6); }
        .input[type="date"] { color-scheme: dark; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-white/60">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
        {label}
      </div>
      <div className="text-white">{value}</div>
    </div>
  );
}
